/**
 * Hourly Breakdown columns — base daily metrics + derived insight columns
 * for customer flow, recharge funnel, and staffing recommendations.
 */

import {
  getDailyBreakdownColumnDefinitions,
} from "./dashboardDailyBreakdownColumns";

const pct = (numerator, denominator) =>
  denominator > 0 ? Math.round((numerator / denominator) * 10000) / 100 : 0;

/** Composite proxy for "customer flow" — unique attempts + new signups in the bucket. */
export const computeCustomerFlowScore = (row) =>
  (row?.uniqueConsultationAttempt || 0) + (row?.totalSignup || 0);

const uniqueFirstRechargeSuccessCount = (row) =>
  row?.uniqueFirstRechargeSuccessCount ?? 0;

const uniqueFirstRechargeAttemptCount = (row) =>
  row?.uniqueFirstRechargeAttemptCount ?? 0;

export const getAttemptToFirstRechargeBreakdown = (row) => {
  const uniqueAttempts = uniqueFirstRechargeAttemptCount(row);
  const uniqueFirstRecharge = uniqueFirstRechargeSuccessCount(row);
  return {
    pct: pct(uniqueFirstRecharge, uniqueAttempts),
    uniqueAttempts,
    uniqueFirstRecharge,
  };
};

export const getAttemptToFirstRechargeTotals = (dataArray = []) => {
  const uniqueAttempts = dataArray.reduce(
    (sum, row) => sum + uniqueFirstRechargeAttemptCount(row),
    0
  );
  const uniqueFirstRecharge = dataArray.reduce(
    (sum, row) => sum + uniqueFirstRechargeSuccessCount(row),
    0
  );
  return {
    pct: pct(uniqueFirstRecharge, uniqueAttempts),
    uniqueAttempts,
    uniqueFirstRecharge,
  };
};

export const PEAK_FLOW_LEVELS = {
  peak: {
    label: "🔥 Peak",
    title: "Peak",
    description:
      "Highest customer flow in this day. This slot had the most unique consultation attempts + signups. Put your strongest consultant coverage here.",
  },
  high: {
    label: "↑ High",
    title: "High",
    description:
      "At least 75% of the day's peak flow. Heavy traffic window — keep extra consultants online so customers are not missed.",
  },
  medium: {
    label: "● Medium",
    title: "Medium",
    description:
      "About 40–75% of the day's peak flow. Normal traffic — standard consultant coverage is usually enough.",
  },
  low: {
    label: "↓ Low",
    title: "Low",
    description:
      "Below 40% of the day's peak flow. Quiet slot — lighter consultant coverage is fine.",
  },
};

export const getPeakFlowMeta = (row, dataset = []) => {
  if (!dataset?.length) {
    return { level: null, label: "—", title: "", description: "" };
  }
  const scores = dataset.map(computeCustomerFlowScore);
  const maxScore = Math.max(...scores, 0);
  if (maxScore === 0) {
    return { level: null, label: "—", title: "", description: "" };
  }
  const score = computeCustomerFlowScore(row);
  let level = "low";
  if (score === maxScore) level = "peak";
  else if (score >= maxScore * 0.75) level = "high";
  else if (score >= maxScore * 0.4) level = "medium";
  return { level, ...PEAK_FLOW_LEVELS[level] };
};

export const HOURLY_DERIVED_COLUMN_DEFINITIONS = [
  {
    key: "customerFlowScore",
    label: "Customer Flow Score",
    type: "derived",
    className: "primary",
    metricGroupStart: true,
    compute: computeCustomerFlowScore,
    description:
      "Combined customer activity in this time bucket: Unique Consultation Attempt + Total Sign up. Higher score = more customer traffic.",
    example: "113 unique attempts + 163 signups in 14:00–16:00 → score 276.",
  },
  {
    key: "signupToFirstRechargePct",
    label: "Signup → 1st Recharge %",
    type: "derivedPercentage",
    className: "primary",
    compute: (row) =>
      pct(row?.firstTimeRechargeCount || 0, row?.totalSignup || 0),
    description:
      "In this time bucket: (1st recharges in bucket ÷ signups in bucket) × 100. Both events are counted in the same hour slot — not a full signup-to-recharge cohort.",
    example: "163 signups and 8 first recharges in 14:00–16:00 → 4.91%.",
  },
  {
    key: "attemptToFirstRechargePct",
    label: "Attempt → 1st Recharge %",
    type: "derivedPercentage",
    className: "primary",
    compute: (row) => getAttemptToFirstRechargeBreakdown(row).pct,
    description:
      "New unique customers who attempted 1st recharge vs those who successfully recharged. Formula: (unique 1st recharge successful ÷ unique 1st recharge attempts) × 100.",
    example:
      "35 unique new customers attempted a 1st recharge and 17 of them succeeded → 48.57%.",
  },
  {
    key: "missedConsultationRatePct",
    label: "Missed Consultation Rate %",
    type: "derivedPercentage",
    className: "error",
    compute: (row) =>
      pct(row?.totalMissedConsultation || 0, row?.totalConsultationAttempt || 0),
    description:
      "Share of consultation attempts that ended as missed in this time bucket.",
    example: "22 missed out of 191 attempts → 11.52%.",
  },
  {
    key: "consultationCompletionRatePct",
    label: "Consultation Completion Rate %",
    type: "derivedPercentage",
    className: "success",
    compute: (row) =>
      pct(
        row?.uniqueCompletedConsultation || 0,
        row?.uniqueConsultationAttempt || 0
      ),
    description:
      "Unique customers who completed at least one consultation vs unique customers who attempted.",
    example: "85 completed / 113 attempted → 75.22%.",
  },
  {
    key: "avgFirstRechargeAmount",
    label: "Avg 1st Recharge Amount",
    type: "derivedCurrency",
    compute: (row) => {
      const count = row?.firstTimeRechargeCount || 0;
      return count > 0 ? (row?.firstTimeRechargeAmount || 0) / count : 0;
    },
    description: "Average rupee value of first-time recharges in this bucket.",
    example: "₹4,800 from 8 first recharges → ₹600 avg.",
  },
  {
    key: "rechargeConversionScore",
    label: "Recharge Conversion Score",
    type: "derived",
    className: "primary",
    metricGroupEnd: true,
    compute: (row) => {
      const firstToSecond = row?.sameDayFirstToSecondRechargeConversionPct || 0;
      const secondToThird = row?.sameDaySecondToThirdRechargeConversionPct || 0;
      const firstCount = row?.firstTimeRechargeCount || 0;
      return Math.round((firstToSecond + secondToThird) * firstCount);
    },
    description:
      "Weighted recharge funnel strength: (1st→2nd % + 2nd→3rd %) × 1st recharge count. Helps find high-conversion hours with volume.",
    example: "40% + 25% with 10 first recharges → score 650.",
  },
  {
    key: "peakFlowIndicator",
    label: "Peak Flow",
    type: "derivedLabel",
    requiresDataset: true,
    compute: (row, dataset) => getPeakFlowMeta(row, dataset).label,
    description:
      "Compares this slot’s customer flow (unique attempts + signups) with the day’s highest slot. Hover a cell for the meaning of Peak, High, Medium, and Low.",
    example:
      "🔥 Peak = highest slot. ↑ High ≥ 75% of peak. ● Medium 40–75%. ↓ Low < 40%.",
  },
  {
    key: "staffingRecommendation",
    label: "Staffing Recommendation",
    type: "derivedLabel",
    requiresDataset: true,
    compute: (row, dataset) => {
      const flowScore = computeCustomerFlowScore(row);
      const firstRecharge = row?.firstTimeRechargeCount || 0;
      const conversionPct =
        row?.sameDayFirstToSecondRechargeConversionPct || 0;
      const missedRate = pct(
        row?.totalMissedConsultation || 0,
        row?.totalConsultationAttempt || 0
      );

      if (!dataset?.length) return "—";

      const maxFlow = Math.max(...dataset.map(computeCustomerFlowScore), 0);
      const isPeakFlow = maxFlow > 0 && flowScore >= maxFlow * 0.75;
      const isHighConversion = conversionPct >= 30 && firstRecharge >= 3;
      const isHighMissed = missedRate >= 15 && flowScore >= maxFlow * 0.5;

      if (isPeakFlow && isHighConversion) {
        return "⭐ Top consultants online";
      }
      if (isPeakFlow && isHighMissed) {
        return "⚠ More consultants needed";
      }
      if (isPeakFlow) {
        return "📈 Increase consultant coverage";
      }
      if (isHighConversion) {
        return "💰 Good recharge window";
      }
      return "—";
    },
    description:
      "Action hint based on flow + recharge conversion + missed rate. Use to schedule top-performing consultants during peak hours.",
    example: "High flow + 40% 1st→2nd conversion → ⭐ Top consultants online.",
  },
];

const HOURLY_DEFAULT_VISIBLE_KEYS = new Set([
  "date",
  "customerFlowScore",
  "totalSignup",
  "uniqueConsultationAttempt",
  "firstTimeRechargeCount",
  "firstTimeRechargeAmount",
  "sameDayFirstToSecondRechargeCount",
  "sameDayFirstToSecondRechargeConversionPct",
  "sameDaySecondToThirdRechargeCount",
  "sameDaySecondToThirdRechargeConversionPct",
  "signupToFirstRechargePct",
  "attemptToFirstRechargePct",
  "rechargeConversionScore",
  "peakFlowIndicator",
  "staffingRecommendation",
]);

export function getHourlyBreakdownColumnDefinitions(isMarketingDashboard) {
  const baseColumns = getDailyBreakdownColumnDefinitions(isMarketingDashboard);
  const derivedInsertIndex = baseColumns.findIndex(
    (col) => col.key === "totalSignup"
  );
  const insertAt = derivedInsertIndex >= 0 ? derivedInsertIndex + 1 : 1;

  return [
    ...baseColumns.slice(0, insertAt),
    ...HOURLY_DERIVED_COLUMN_DEFINITIONS,
    ...baseColumns.slice(insertAt),
  ];
}

export function buildHourlyBreakdownColumnVisibilityDefaults(isMarketingDashboard) {
  const columns = getHourlyBreakdownColumnDefinitions(isMarketingDashboard);
  const vis = {};
  columns.forEach((col) => {
    vis[col.key] = HOURLY_DEFAULT_VISIBLE_KEYS.has(col.key);
  });
  return vis;
}

export function isDerivedColumn(column) {
  return (
    column?.type === "derived" ||
    column?.type === "derivedPercentage" ||
    column?.type === "derivedCurrency" ||
    column?.type === "derivedLabel"
  );
}

export function getDerivedCellValue(row, column, dataset = []) {
  if (!column?.compute) return 0;
  return column.compute(row, dataset);
}

export function getDerivedTotalsValue(column, dataArray) {
  if (column.type === "derivedLabel") {
    return "—";
  }

  if (column.type === "derivedPercentage") {
    return getDerivedTotalsValue({ ...column, type: "derived" }, dataArray);
  }

  if (column.key === "signupToFirstRechargePct") {
    const numerator = dataArray.reduce(
      (sum, row) => sum + (row.firstTimeRechargeCount || 0),
      0
    );
    const denominator = dataArray.reduce(
      (sum, row) => sum + (row.totalSignup || 0),
      0
    );
    return pct(numerator, denominator);
  }

  if (column.key === "attemptToFirstRechargePct") {
    return getAttemptToFirstRechargeTotals(dataArray).pct;
  }

  if (column.key === "missedConsultationRatePct") {
    const numerator = dataArray.reduce(
      (sum, row) => sum + (row.totalMissedConsultation || 0),
      0
    );
    const denominator = dataArray.reduce(
      (sum, row) => sum + (row.totalConsultationAttempt || 0),
      0
    );
    return pct(numerator, denominator);
  }

  if (column.key === "consultationCompletionRatePct") {
    const numerator = dataArray.reduce(
      (sum, row) => sum + (row.uniqueCompletedConsultation || 0),
      0
    );
    const denominator = dataArray.reduce(
      (sum, row) => sum + (row.uniqueConsultationAttempt || 0),
      0
    );
    return pct(numerator, denominator);
  }

  if (column.key === "avgFirstRechargeAmount") {
    const totalAmount = dataArray.reduce(
      (sum, row) => sum + (row.firstTimeRechargeAmount || 0),
      0
    );
    const totalCount = dataArray.reduce(
      (sum, row) => sum + (row.firstTimeRechargeCount || 0),
      0
    );
    return totalCount > 0 ? Math.round(totalAmount / totalCount) : 0;
  }

  if (column.key === "rechargeConversionScore") {
    const totalFirst = dataArray.reduce(
      (sum, row) => sum + (row.firstTimeRechargeCount || 0),
      0
    );
    const totalFirstToSecond = dataArray.reduce(
      (sum, row) => sum + (row.sameDayFirstToSecondRechargeCount || 0),
      0
    );
    const totalSecond = dataArray.reduce(
      (sum, row) => sum + (row.secondRechargeCount || 0),
      0
    );
    const totalSecondToThird = dataArray.reduce(
      (sum, row) => sum + (row.sameDaySecondToThirdRechargeCount || 0),
      0
    );
    const firstToSecondPct = totalFirst > 0 ? (totalFirstToSecond / totalFirst) * 100 : 0;
    const secondToThirdPct = totalSecond > 0 ? (totalSecondToThird / totalSecond) * 100 : 0;
    return Math.round((firstToSecondPct + secondToThirdPct) * totalFirst);
  }

  if (typeof column.compute === "function") {
    return dataArray.reduce(
      (sum, row) => sum + (Number(column.compute(row, dataArray)) || 0),
      0
    );
  }

  return 0;
}
